import { useTranslation } from 'react-i18next';

import {
    CustomerNameCell,
    DealCheckboxCell,
    DealNameCell,
    DealNoteCell,
    DealObjectCell,
    DealTableCell,
    useDealsTable,
} from '@crm/entities/deal';
import { InWorkOrFinishDeal, LostDeal, ReturnDealsToPipeline } from '@crm/features';
import ArchiveIcon from '@crm/shared/assets/icons/archive.svg?react';
import CircleArrowRightIcon from '@crm/shared/assets/icons/circle-arrow-right.svg?react';
import LinkBackwardIcon from '@crm/shared/assets/icons/link-backward.svg?react';
import { cn, getPluralized, tw } from '@crm/shared/lib';
import {
    Button,
    SelectionActionsBar,
    SortableTableHead,
    SvgIcon,
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@crm/shared/ui';

import { PhoneCell } from './PhoneCell';
import { PostponedUntilCell } from './PostponedUntilCell';
import { DEALS_TABLE_SORT_FIELDS } from '../../model/consts';

import type {
    TPostponedDealsData,
    TPostponedDealsSortDirection,
    TPostponedDealsSortField,
} from '@crm/entities/deal';

export const DealsTable = ({
    allowClear,
    dealItems = [],
    pipelineColumns,
    direction,
    onSortChange,
    sort,
}: {
    allowClear: boolean;
    dealItems?: TPostponedDealsData['items'];
    pipelineColumns: TPostponedDealsData['pipelineColumnsData']['columns'];
    direction: TPostponedDealsSortDirection | null;
    onSortChange: (sortState: {
        direction: TPostponedDealsSortDirection | null;
        sort: TPostponedDealsSortField | null;
    }) => void;
    sort: TPostponedDealsSortField | null;
}) => {
    const { t } = useTranslation();
    // Trade-off: для каждой строки создаются экземпляры feature-компонентов с диалогами.
    // Если количество одновременно отрендеренных строк заметно вырастет, действия стоит
    // снова перевести на один управляемый диалог на уровне таблицы.
    const tableGridTemplateColumnsStyles = tw(
        'grid-cols-[24px_fit-content(400px)_130px_130px_140px_150px_minmax(220px,1fr)_100px]'
    );
    const {
        selectedDealIds,
        isAllDealsSelected,
        isSomeDealsSelected,
        handleSelectAllDeals,
        handleSelectDeal,
        clearSelectedDeals,
        handleRowClick,
        handleRowKeyDown,
    } = useDealsTable({ dealItems });

    return (
        <div className="relative h-full">
            <Table
                containerClassName="h-full overflow-y-auto"
                className={cn('grid min-w-280 gap-x-3', tableGridTemplateColumnsStyles)}
            >
                <TableHeader className="sticky top-0 z-10 col-span-full grid grid-cols-subgrid bg-white [&_th]:h-auto [&_th]:py-2 [&_th]:text-xs [&_th]:font-normal [&_th]:text-smtr-main-tertiary-100 [&_tr]:border-0">
                    <TableRow className="col-span-full grid grid-cols-subgrid items-center hover:bg-transparent">
                        <DealCheckboxCell
                            variant="header"
                            checked={isAllDealsSelected}
                            indeterminate={isSomeDealsSelected && !isAllDealsSelected}
                            aria-label={t('crm.common.dealsTable.selectAllAriaLabel')}
                            onCheckedChange={handleSelectAllDeals}
                        />
                        <TableHead className="pl-1">
                            {t('crm.postponedPage.table.header.dealName')}
                        </TableHead>
                        <TableHead className="px-1">
                            {t('crm.postponedPage.table.header.customerName')}
                        </TableHead>
                        <TableHead className="px-0">
                            {t('crm.postponedPage.table.header.phone')}
                        </TableHead>
                        <SortableTableHead
                            activeSort={sort}
                            align="center"
                            allowClear={allowClear}
                            direction={direction}
                            sortField={DEALS_TABLE_SORT_FIELDS.postponedUntil}
                            onSortChange={onSortChange}
                        >
                            {t('crm.postponedPage.table.header.postponedUntil')}
                        </SortableTableHead>
                        <TableHead className="px-0">
                            {t('crm.postponedPage.table.header.object')}
                        </TableHead>
                        <TableHead className="px-1">
                            {t('crm.postponedPage.table.header.note')}
                        </TableHead>
                        {/* <SortableTableHead
                            activeSort={sort}
                            allowClear={allowClear}
                            direction={direction}
                            sortField={DEALS_TABLE_SORT_FIELDS.lastContactAt}
                            onSortChange={onSortChange}
                        >
                            Последний контакт
                        </SortableTableHead> */}
                        <TableHead className="px-1 text-right">
                            {t('crm.postponedPage.table.header.actions')}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className="col-span-full grid grid-cols-subgrid gap-y-1 [&_td]:py-2">
                    {dealItems.map((deal) => (
                        <TableRow
                            key={deal.id}
                            className="col-span-full grid h-10 cursor-pointer grid-cols-subgrid items-center rounded-md border-0 hover:bg-smtr-main-bgGray-50 data-[state=selected]:bg-smtr-main-accent-50"
                            data-state={selectedDealIds.has(deal.id) ? 'selected' : undefined}
                            tabIndex={0}
                            aria-label={t('crm.common.dealsTable.row.openAriaLabel', {
                                dealName: deal.name,
                            })}
                            onClick={(event) => handleRowClick(event, deal.id)}
                            onKeyDown={(event) => handleRowKeyDown(event, deal.id)}
                        >
                            <DealCheckboxCell
                                checked={selectedDealIds.has(deal.id)}
                                aria-label={t('crm.common.dealsTable.row.selectAriaLabel', {
                                    dealName: deal.name,
                                })}
                                onCheckedChange={(checked) => handleSelectDeal(deal.id, checked)}
                            />
                            <DealNameCell dealName={deal.name} />
                            <CustomerNameCell customerName={deal.customerName} />

                            <PhoneCell phone={deal.phone ?? '-'} />
                            <PostponedUntilCell
                                dealId={deal.id}
                                postponedUntil={deal.context?.postponedUntil}
                            />
                            <DealObjectCell
                                objectType={deal.objectType}
                                area={deal.area}
                                unit={deal.unit}
                            />
                            {/* <CommentCell dealId={deal.id} comment={deal.requestEssence} /> */}
                            <DealNoteCell textContent={deal.comment ?? '-'} />
                            {/* <DealTableCell isEmpty={!deal.context?.lastContactAt} className="px-1">
                                {DateHelpers.formatDisplayDate(
                                    new Date(deal.context?.lastContactAt)
                                )}
                            </DealTableCell> */}
                            <DealTableCell className="px-1">
                                <div className="flex items-center justify-end gap-1">
                                    <Tooltip>
                                        <ReturnDealsToPipeline
                                            dealIds={[deal.id]}
                                            columns={pipelineColumns}
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-blue-50 text-blue-500 hover:bg-blue-100 hover:text-blue-500"
                                                            aria-label={t(
                                                                'crm.common.actions.returnToPipeline'
                                                            )}
                                                        >
                                                            <SvgIcon
                                                                icon={LinkBackwardIcon}
                                                                size="16"
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.returnToPipeline')}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <LostDeal
                                            dealIds={[deal.id]}
                                            invalidationScope="postponed"
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-smtr-border-100 text-smtr-main-tertiary-100"
                                                            aria-label={t(
                                                                'crm.common.actions.markLost'
                                                            )}
                                                        >
                                                            <SvgIcon icon={ArchiveIcon} size="16" />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.markLost')}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <InWorkOrFinishDeal
                                            dealIds={[deal.id]}
                                            invalidationScope="postponed"
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-smtr-border-100 text-smtr-main-tertiary-100"
                                                            aria-label={t(
                                                                'crm.common.actions.sendToWork'
                                                            )}
                                                        >
                                                            <SvgIcon
                                                                icon={CircleArrowRightIcon}
                                                                size="16"
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.sendToWork')}
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </DealTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {selectedDealIds.size > 0 && (
                <SelectionActionsBar
                    label={t('crm.common.dealsTable.selection.label', {
                        deals: getPluralized(selectedDealIds.size, {
                            one: t('crm.common.plurals.deal.one'),
                            few: t('crm.common.plurals.deal.few'),
                            many: t('crm.common.plurals.deal.many'),
                        }),
                    })}
                    onClose={clearSelectedDeals}
                >
                    <ReturnDealsToPipeline
                        dealIds={Array.from(selectedDealIds)}
                        columns={pipelineColumns}
                        trigger={
                            <Button type="button" className="bg-primary">
                                {t('crm.common.actions.returnToPipeline')}
                            </Button>
                        }
                    />
                    <LostDeal
                        dealIds={Array.from(selectedDealIds)}
                        invalidationScope="postponed"
                        onSuccess={clearSelectedDeals}
                        trigger={
                            <Button type="button" className="bg-gray-400 hover:bg-gray-600">
                                {t('crm.common.actions.markLost')}
                            </Button>
                        }
                    />
                    <InWorkOrFinishDeal
                        dealIds={Array.from(selectedDealIds)}
                        invalidationScope="postponed"
                        onSuccess={clearSelectedDeals}
                        trigger={
                            <Button type="button">{t('crm.common.actions.sendToWork')}</Button>
                        }
                    />
                </SelectionActionsBar>
            )}
        </div>
    );
};
